import React, { useEffect, useState } from 'react';
import { Chip, Spinner, Text } from 'folds';
import { useMatrixClient } from '../../hooks/useMatrixClient';
import { fetchUsersByRoles } from './CurveWork';
import { debugError } from '../../utils/debugLog';
import { getMxIdLocalPart } from '../../utils/matrix';
import * as css from './RoleInviteList.css';
import robotLogo from '../../../../public/icon/新增機器人按鈕.png';

type RoleInviteListProps = {
  sroomid: string;
  role: string;
};

export function RoleInviteList({ sroomid, role }: RoleInviteListProps) {
  const mx = useMatrixClient();
  const [invitableMembers, setInvitableMembers] = useState<string[]>([]);
  const [loadingUserId, setLoadingUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const userId = mx.getUserId();
        if (!userId) return;

        const room = mx.getRoom(sroomid);
        let project: string | undefined;
        try {
          const projectEvent = room?.currentState.getStateEvents('m.room.project', '');
          const content = projectEvent?.getContent();
          if (content && typeof content.project === 'string') {
            project = content.project;
          }
        } catch (e) {
          debugError('RoleInviteList: get project state failed:', e);
        }

        if (!project) return;

        const users: string[] = await fetchUsersByRoles(mx, role, project);
        const filtered = users.filter((uid) => {
          const member = room?.currentState.members[uid];
          return !member || member.membership !== 'join';
        });
        setInvitableMembers(filtered);
      } catch (e) {
        debugError('RoleInviteList: fetch failed:', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [mx, sroomid, role]);

  const handleInvite = async (userId: string) => {
    if (loadingUserId !== null) return;
    setLoadingUserId(userId);
    try {
      await mx.invite(sroomid, userId);
      setInvitableMembers((prev) => prev.filter((uid) => uid !== userId));
    } catch (e) {
      debugError('RoleInviteList: invite failed:', e);
    } finally {
      setLoadingUserId(null);
    }
  };

  if (isLoading) {
    return <Spinner size="200" />;
  }

  if (invitableMembers.length === 0) {
    return <div className={css.EmptyMsg}>無可邀請成員</div>;
  }

  return (
    <div className={css.List}>
      {invitableMembers.map((userId) => (
        <div key={userId} className={css.MemberRow}>
          <img src={robotLogo} style={{ width: '24px', height: '24px' }} alt="AI" />
          <span className={css.MemberName}>{getMxIdLocalPart(userId) ?? userId}</span>
          {loadingUserId === userId ? (
            <Spinner size="200" />
          ) : (
            <Chip
              size="400"
              variant="Success"
              radii="300"
              onClick={() => handleInvite(userId)}
            >
              <Text size="T200">+</Text>
            </Chip>
          )}
        </div>
      ))}
    </div>
  );
}
